import { BillingPlugin, IAPProduct, buildStoreConfirmationFailure } from 'services/iap.service';
import { SubscriptionManager } from 'services/subscription-manager.service';
import { describeError } from '@cf-shared/error-describe';
import { CouchLoginReason } from 'services/couch-login-storm';
import { isAppleCrossGradeScheduled, runStoreCrossGradeAsync } from 'services/store-cross-grade-flow';
import { PlanOption } from 'services/paypro-price-fetcher.service';
import { AppEnvironment } from 'environments/environment';
import { resolvePaddleCurrentPlanMonths } from './paddle-subscription-policy';

// SubscriptionManager ↔ subscription-strategy circular import resolves
// cleanly because this file uses SubscriptionManager only as a type
// annotation (constructor param), not as a runtime value. Same shape as
// the camera-strategy ↔ camera.service pairing.

export type SubscriptionProvider = 'googlePlay' | 'payPro' | 'apple' | 'paddle';

export interface SwitchPlanResult {
  success: boolean;
  canceled?: boolean;
  error?: string;
  linkedEmail?: string;
  /** Apple downgrade only — the requested change was queued at end-of-cycle
   *  rather than applied immediately. Caller surfaces a pending-change notice
   *  rather than an immediate-success alert. */
  scheduled?: boolean;
  /** True when the store completed the plan change and the failure recorded in
   *  `error` happened AFTER that, in our own confirmation step. The change is
   *  not lost — the store has it, and the server picks it up again at the next
   *  sign-in — so nothing shown to the buyer may say it failed.
   *
   *  Left unset when the store SETTLED the question, because there the change
   *  really did not happen and the terminal wording is correct. Same rule and
   *  same reasoning as `IAPPurchaseResult.confirmationFailedAfterPurchase`; see
   *  `design-docs/iap-subscriptions.md`, "What the buyer is told". */
  confirmationFailedAfterPurchase?: boolean;
}

/** What a cancel answers. `alreadyScheduled` is Paddle-only and comes back on a
 *  SUCCESS: Paddle had this cancellation scheduled already, so the customer is
 *  cancelled but we did not do it just now. The caller uses it to pick the right
 *  message — see #4629, where reporting that state as a failure told customers
 *  their cancellation had not happened when it had. */
export interface CancelSubscriptionResult {
  error?: string;
  alreadyScheduled?: boolean;
}

export interface CancelSubscriptionParams {
  reasonId?: number;
  reasonText?: string;
}

/** Every operation whose behaviour depends on the provider. The manager keeps
 *  the state and delegates each of these; `createSubscriptionStrategy` is the
 *  one place the provider list is written down. */
export interface SubscriptionStrategy {
  switchPlan(): Promise<SwitchPlanResult>;
  cancel(params?: CancelSubscriptionParams): Promise<CancelSubscriptionResult>;
  resume(): Promise<{ error?: string }>;
  /** Fill `manager.currentPlan` / `manager.otherPlan` for this provider. */
  loadPlans(): Promise<void>;
}

/** Present StoreKit's native Manage Subscriptions sheet, which works in BOTH
 *  sandbox (TestFlight / App Review) and production. Apple cancel and resume
 *  both happen there (resume = re-enable auto-renew). The old itms-apps:// URL
 *  hands off to System Settings > Subscriptions, which only lists PRODUCTION
 *  subscriptions — a sandbox subscription can't be viewed or cancelled there —
 *  so it is only the fallback when the native sheet can't present. */
async function openAppleManageSubscriptionsAsync(manager: SubscriptionManager): Promise<{ error?: string }> {
  const result = await manager.iapService.showManageSubscriptionsAsync();
  if (result.error) {
    window.open('itms-apps://apps.apple.com/account/subscriptions', '_self');
  }
  return {};
}

/** Turn a store product into the plan option the profile page shows. Apple and
 *  Google Play differ only in which identifier becomes the plan's id: Apple's
 *  product id, Google Play's base plan id. */
function iapProductToPlanOption(p: IAPProduct, id: string): PlanOption {
  return {
    id,
    name: p.title,
    title: p.numMonths === 1 ? 'Monthly' : 'Yearly',
    buyUrl: '',
    price: p.priceMicros / 1_000_000,
    currency: p.currencyCode,
    priceText: p.priceText,
    preDiscount: p.preDiscount,
    saving: p.saving,
    numMonths: p.numMonths,
  };
}

/** Refresh the local CouchDB session after a server-side subscription
 *  change. Failure here is non-fatal — the cross-grade has already
 *  landed server-side, the local refresh is just to pick up the new
 *  entitlements without waiting for the next refreshAsync tick. Logs
 *  to Sentry and swallows so a transient network blip doesn't paint
 *  the user an error toast for an operation that actually succeeded. */
async function safeRefreshLocalSession(manager: SubscriptionManager): Promise<void> {
  try {
    await manager.auth.attemptCouchLoginAsync(CouchLoginReason.SubscriptionPlanSwitch);
  } catch (error) {
    manager.logging.trackSilentProblem('switchPlan login refresh failed', {
      message: describeError(error),
    });
  }
}

// ── Apple ─────────────────────────────────────────────────────────────

/**
 * iOS in-app cross-grade via StoreKit2. `BillingPlugin.updateSubscription`
 * calls `Product.purchase()` on the target tier; StoreKit treats that as
 * an implicit same-group cross-grade when both products share a
 * `subscriptionGroupID`. The `offerToken` and `oldPurchaseToken`
 * parameters are Android-only and ignored on iOS.
 */
export class AppleSubscriptionStrategy implements SubscriptionStrategy {
  constructor(private manager: SubscriptionManager) {}

  async switchPlan(): Promise<SwitchPlanResult> {
    const otherPlan = this.manager.otherPlan;
    if (!otherPlan) return { success: false, error: 'No alternate plan.' };

    // for Apple users, PlanOption.id carries the Apple productId —
    // populated by AppleSubscriptionStrategy.loadPlans below.
    const productId = otherPlan.id;

    // the order of the steps, and when the buyer counts as charged, live in
    // runStoreCrossGradeAsync; this supplies only what is Apple- and personal-plan-specific.
    return runStoreCrossGradeAsync({
      runStoreCallAsync: () => BillingPlugin.updateSubscription({
        productId,
        offerToken: '',
        oldPurchaseToken: '',
      }),
      // staleTransaction:true signals a StoreKit2 sandbox short-circuit (returned
      // a previously-finished historical tx without prompting auth). The flow treats
      // it as canceled for caller UX; this logs it so production regressions stay visible.
      reportStaleTransaction: () => {
        this.manager.logging.trackSilentProblem('Apple switchPlan returned stale transaction', {
          requestedProductId: productId,
        });
      },
      reportUnverifiedOnDevice: response => {
        this.manager.logging.trackSilentProblem('Apple switchPlan unverified on device', {
          requestedProductId: productId,
          reason: response.unverifiedReason,
        });
      },
      validateAsync: response => this.manager.cloudFunctions.appleValidateAsync({
        transactionId: response.purchaseToken,
        originalTransactionId: response.orderId || response.purchaseToken,
        signedTransaction: response.signedTransaction,
      }),
      // the personal rail needs no device record: couchLogin re-validates the
      // account's own appleData/googlePlayData, and the server parks a claim
      // when it cannot reach the store (#5611 added these for the coach rail).
      rememberUnconfirmedPurchase: () => {},
      onServerAnswered: () => {},
      buildValidationFailure: buildStoreConfirmationFailure,
      isScheduled: validation => isAppleCrossGradeScheduled(validation, productId),
      finishAsync: () => safeRefreshLocalSession(this.manager),
    });
  }

  cancel(): Promise<CancelSubscriptionResult> {
    return openAppleManageSubscriptionsAsync(this.manager);
  }

  resume(): Promise<{ error?: string }> {
    return openAppleManageSubscriptionsAsync(this.manager);
  }

  async loadPlans(): Promise<void> {
    const productIds = AppEnvironment.appleProductIds;
    if (!productIds?.length) return;

    const iapProducts = await this.manager.iapService.getProductsAsync(productIds);

    // match current plan by Apple product ID stored in user data
    const appleProductId = this.manager.auth.couch?.appleData?.productId;
    const currentIAP = appleProductId ? iapProducts.find(p => p.id === appleProductId) : null;
    const otherIAP = currentIAP ? iapProducts.find(p => p !== currentIAP) : null;

    this.manager.currentPlan = currentIAP ? iapProductToPlanOption(currentIAP, currentIAP.id) : null;
    this.manager.otherPlan = otherIAP ? iapProductToPlanOption(otherIAP, otherIAP.id) : null;
  }
}

// ── Google Play ───────────────────────────────────────────────────────

/**
 * Android in-app cross-grade. Behaviourally equivalent to the original
 * IAPService.switchPlanAsync Android branch, transplanted into the
 * strategy. The `CHARGE_FULL_PRICE` proration mode lives in the Android
 * Java impl (BillingPlugin.java), not the JS bridge — the JS side just
 * passes the new product + old purchase token.
 *
 * A thrown exception is answered in one of two ways, and which one it gets
 * depends on whether the store had already made the change and taken the
 * money. From before that point it is left to propagate to user-profile.ts's
 * onSwitchPlanClick outer try/catch, which surfaces it via
 * LoggingService.trackAbnormalException with the full error object — better
 * than catching it here and downgrading it to a message-only string. From
 * after it, that same outer catch would show a charged buyer "Failed to switch
 * the plan.", which is false, so `runStoreCrossGradeAsync` answers it instead.
 * See `store-cross-grade-flow.ts`.
 */
export class GooglePlaySubscriptionStrategy implements SubscriptionStrategy {
  // the store product behind manager.otherPlan, set by loadPlans. switchPlan
  // needs the product itself because the billing library wants its offerToken.
  private otherProduct: IAPProduct | null = null;

  constructor(private manager: SubscriptionManager) {}

  async switchPlan(): Promise<SwitchPlanResult> {
    const newProduct = this.otherProduct;
    if (!newProduct) return { success: false, error: 'No alternate plan.' };

    const oldPurchaseToken = this.manager.auth.couch?.googlePlayData?.purchaseToken;
    if (!oldPurchaseToken) {
      return { success: false, error: 'No existing subscription found.' };
    }

    return runStoreCrossGradeAsync({
      getAccountIdAsync: () => this.manager.iapService.getObfuscatedAccountId(),
      runStoreCallAsync: accountId => BillingPlugin.updateSubscription({
        productId: newProduct.id,
        offerToken: newProduct.offerToken,
        oldPurchaseToken,
        accountId,
      }),
      // the Android plugin never sends this today; reported rather than dropped
      // in case it ever does.
      reportUnverifiedOnDevice: response => {
        this.manager.logging.trackSilentProblem('Google switchPlan unverified on device', {
          requestedProductId: newProduct.id,
          reason: response.unverifiedReason,
        });
      },
      validateAsync: (response, accountId) => this.manager.cloudFunctions.googlePlayValidateAsync({
        purchaseToken: response.purchaseToken,
        productId: response.productId || newProduct.id,
        obfuscatedAccountId: accountId,
      }),
      // the personal rail needs no device record: couchLogin re-validates the
      // account's own appleData/googlePlayData, and the server parks a claim
      // when it cannot reach the store (#5611 added these for the coach rail).
      rememberUnconfirmedPurchase: () => {},
      onServerAnswered: () => {},
      buildValidationFailure: buildStoreConfirmationFailure,
      finishAsync: () => safeRefreshLocalSession(this.manager),
    });
  }

  cancel(): Promise<CancelSubscriptionResult> {
    return this.manager.cloudFunctions.googlePlayCancelAsync({});
  }

  async resume(): Promise<{ error?: string }> {
    // GP resume = open Play Store subscriptions page
    window.open('https://play.google.com/store/account/subscriptions', '_blank');
    return {};
  }

  async loadPlans(): Promise<void> {
    const productId = AppEnvironment.googlePlayProductId;
    if (!productId) return;

    const iapProducts = await this.manager.iapService.getProductsAsync([productId]);

    const basePlanId = this.manager.auth.couch?.googlePlayData?.basePlanId;
    const currentIAP = basePlanId ? iapProducts.find(p => p.basePlanId === basePlanId) : null;
    const otherIAP = currentIAP ? iapProducts.find(p => p !== currentIAP) : null;

    this.manager.currentPlan = currentIAP ? iapProductToPlanOption(currentIAP, currentIAP.basePlanId) : null;
    if (otherIAP) {
      this.manager.otherPlan = iapProductToPlanOption(otherIAP, otherIAP.basePlanId);
      this.otherProduct = otherIAP;
    } else {
      this.manager.otherPlan = null;
      this.otherProduct = null;
    }
  }
}

// ── PayPro ────────────────────────────────────────────────────────────

// all known PayPro product IDs → billing period (numMonths)
// covers all environments (dev, test, prod) and all product generations
const PAYPRO_PRODUCT_NUM_MONTHS: Record<string, number> = {
  // Gen 1 — Cybernetic Fitness (oldest)
  '76232': 1, '76233': 12,  // dev
  '76834': 1, '76835': 12,  // test
  '76836': 1, '76837': 12,  // prod
  // Gen 2 — Cybernetic Fitness
  '89631': 1, '89632': 12,  // dev
  '89704': 1, '89705': 12,  // test
  '89706': 1, '89707': 12,  // prod
  // Gen 3 — MH Physique (current)
  '106644': 1, '106645': 12, // dev
  '106654': 1, '106655': 12, // test
  '106666': 1, '106667': 12, // prod
  // Other
  '79908': 1,                 // test product
};

/**
 * PayPro cloud-function-driven plan change. Adapts the cloud function's
 * `{ error?: string }` shape into `SwitchPlanResult`. Behaviourally
 * equivalent to the original `subscription-manager.service.ts` PayPro
 * branch.
 */
export class PayProSubscriptionStrategy implements SubscriptionStrategy {
  constructor(private manager: SubscriptionManager) {}

  async switchPlan(): Promise<SwitchPlanResult> {
    const otherPlan = this.manager.otherPlan;
    if (!otherPlan) return { success: false, error: 'No alternate plan.' };

    const result = await this.manager.cloudFunctions.userSubscriptionAsync({
      action: 'ChangeProduct',
      newProductId: parseInt(otherPlan.id, 10),
    });
    return result.error ? { success: false, error: result.error } : { success: true };
  }

  /** The caller must collect the cancellation reason first
   *  (manager.needsCancellationReason is true for PayPro). */
  cancel(params?: CancelSubscriptionParams): Promise<CancelSubscriptionResult> {
    return this.manager.cloudFunctions.payProUnsubscribeAsync({
      cancellationReasonId: params?.reasonId,
      reasonText: params?.reasonText,
    });
  }

  resume(): Promise<{ error?: string }> {
    return this.manager.cloudFunctions.payProResubscribeAsync({ reasonText: undefined });
  }

  async loadPlans(): Promise<void> {
    const planOptions = await this.manager.payProPriceFetcher.fetchPricesAsync();

    const couch = this.manager.auth.couch;
    // if user logged out while fetching prices
    if (!couch) return;

    // find the currently active plan
    let currentPlan = planOptions.find(p => p.id.toString() === couch.payProProductId) ?? null;

    // if exact product ID match fails, remap legacy product IDs by billing period
    if (!currentPlan && couch.payProProductId) {
      const numMonths = PAYPRO_PRODUCT_NUM_MONTHS[couch.payProProductId];
      if (numMonths) {
        currentPlan = planOptions.find(p => p.numMonths === numMonths) ?? null;
      }
    }

    if (!currentPlan) {
      console.warn(`Cannot match payProProductId '${couch.payProProductId}' to plan options [${planOptions.map(p => p.id).join(', ')}]`);
    }

    this.manager.currentPlan = currentPlan;
    this.manager.otherPlan = planOptions.find(p => p !== currentPlan) ?? null;
  }
}

// ── Paddle ────────────────────────────────────────────────────────────

/**
 * Paddle web subscription strategy. Switching between the monthly and yearly
 * plan runs through the `paddleManageSubscription` callable's `SwitchPlan`
 * action, which PATCHes the subscription's line item to the other price with
 * immediate proration (#4189 Phase 3).
 *
 * The trial guard mirrors PayPro's: Paddle rejects a plan change on a trialing
 * subscription, so we refuse up-front with the same `trial_upgrade_not_supported`
 * code the profile page already maps to the "not supported during trial"
 * message, rather than letting the API round-trip fail with a raw error.
 */
export class PaddleSubscriptionStrategy implements SubscriptionStrategy {
  constructor(private manager: SubscriptionManager) {}

  async switchPlan(): Promise<SwitchPlanResult> {
    const otherPlan = this.manager.otherPlan;
    if (!otherPlan) return { success: false, error: 'No alternate plan.' };

    // if the PADDLE subscription is still in its free trial, Paddle refuses the
    // change. Same rule the server's switch body and the admin matrix ask, so
    // this client-side short-circuit cannot refuse something they would allow.
    if (this.manager.isPaddleSubscriptionInFreeTrial) {
      return { success: false, error: 'trial_upgrade_not_supported' };
    }

    // otherPlan.id is the Paddle price id (`pri_…`) the plan loader stored
    const result = await this.manager.cloudFunctions.paddleManageSubscriptionAsync({
      action: 'SwitchPlan',
      newPriceId: otherPlan.id,
    });
    if (result.error) return { success: false, error: result.error };

    // pick up the new plan in the local session so the Type row / badge update
    await safeRefreshLocalSession(this.manager);
    return { success: true };
  }

  cancel(): Promise<CancelSubscriptionResult> {
    // Paddle cancels at the end of the billing period through our server; no
    // cancellation reason is collected (needsCancellationReason stays false).
    return this.manager.cloudFunctions.paddleManageSubscriptionAsync({ action: 'Cancel' });
  }

  /** Paddle resume = un-schedule the pending change, or un-pause, server-side.
   *  The manager may call this even when `provider` is unknown — see
   *  SubscriptionManager.resumeAsync — so it must not depend on loaded state. */
  resume(): Promise<{ error?: string }> {
    return this.manager.cloudFunctions.paddleManageSubscriptionAsync({ action: 'Resume' });
  }

  async loadPlans(): Promise<void> {
    // #4189: build the plan options from what the server resolved. The server
    // maps the stored Paddle price id → plan name and the monthly ↔ yearly
    // switch target, so there is no second price→period map on the client.
    //
    // We deliberately do NOT run a Paddle.js PricePreview here. The profile page
    // does not otherwise load the Paddle SDK, and the purchase-time price
    // fetcher is keyed to the rollout dispatcher (which decides the rail for a
    // NEW purchase), so it is the wrong source for an existing subscriber's
    // plan pair. The plans therefore carry no `priceText`; the UI that would
    // show a price (the upgrade banner and the current-plan notes) is gated on
    // `priceText` being present, and the Paddle switch confirmation uses copy
    // that does not quote a price. Paddle shows the exact amount on its own
    // checkout / invoice.
    const planName = this.manager.serverPlanName;
    if (!planName) {
      this.manager.currentPlan = null;
      this.manager.otherPlan = null;
      return;
    }
    const switchTarget = this.manager.paddleSwitchTarget;
    const toPlan = (id: string, title: string, numMonths: number): PlanOption => ({
      id,
      name: title,
      title,
      buyUrl: '',
      price: 0,
      currency: '',
      priceText: '',
      numMonths,
    });
    const currentMonths = resolvePaddleCurrentPlanMonths(switchTarget?.numMonths, planName);
    this.manager.currentPlan = toPlan(this.manager.auth.couch?.paddleData?.priceId ?? '', planName, currentMonths);
    // otherPlan arms canSwitchPlan — only set it when the server actually gave
    // us a switch target (a known price pair for this subscription's env).
    this.manager.otherPlan = switchTarget
      ? toPlan(switchTarget.priceId, switchTarget.planName, switchTarget.numMonths)
      : null;
  }
}

// ── Factory ───────────────────────────────────────────────────────────

/** Build the concrete SubscriptionStrategy for the manager's current
 *  provider. Called by SubscriptionManager's lazy strategy getter and
 *  re-invoked whenever `provider` changes (refreshAsync invalidates the
 *  cached strategy). Throws on an unknown provider rather than silently
 *  returning a no-op strategy — see CLAUDE.md's "unexpected conditions
 *  must surface" rule. */
export function createSubscriptionStrategy(manager: SubscriptionManager): SubscriptionStrategy {
  switch (manager.provider) {
    case 'apple':      return new AppleSubscriptionStrategy(manager);
    case 'googlePlay': return new GooglePlaySubscriptionStrategy(manager);
    case 'payPro':     return new PayProSubscriptionStrategy(manager);
    case 'paddle':     return new PaddleSubscriptionStrategy(manager);
    default: throw new Error(`Unknown subscription provider: ${manager.provider}`);
  }
}
